import { useEffect, useRef, useState } from 'react';

import { apiClient, type YoutubeCaption } from '@/services';

interface UseYoutubeCaptionsResult {
  captions: YoutubeCaption[];
  loading: boolean;
  error: string;
}

/**
 * Subtitles for the video currently on screen. Walking a result queue returns to the same video
 * often enough — several matches can share one — that the track is worth keeping for the lifetime
 * of the panel.
 */
export function useYoutubeCaptions(videoId: string | undefined): UseYoutubeCaptionsResult {
  const [captions, setCaptions] = useState<YoutubeCaption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cacheRef = useRef(new Map<string, YoutubeCaption[]>());

  useEffect(() => {
    if (!videoId) {
      setCaptions([]);
      setLoading(false);
      setError('');
      return;
    }

    const cached = cacheRef.current.get(videoId);
    if (cached) {
      setCaptions(cached);
      setLoading(false);
      setError('');
      return;
    }

    let cancelled = false;
    setCaptions([]);
    setLoading(true);
    setError('');
    apiClient.getYoutubeCaptions(videoId)
      .then((track) => {
        if (cancelled) return;
        const loadedCaptions = track.captions ?? [];
        cacheRef.current.set(videoId, loadedCaptions);
        setCaptions(loadedCaptions);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Subtitles are unavailable for this video.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [videoId]);

  return { captions, loading, error };
}
